import { belowBreakpointOrEqual, Button, ImgSelect, ImgThemeConsumer, Loader, Modal, Tooltip } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from 'react';
import { ImgI18NTranslatedComponentProps, translate } from "services/ImgI18N";
import ImageCrop, { IImageCrop } from "./ImageCrop";
import ImageFilter, { IImageFilter, IImageFilterProps } from "./ImageFilter";
import { MediaSelectionSC as T } from './MediaSelectionSC';

interface AspectOption {
    value: number;
    label: string;
}
const aspects: AspectOption[] = [
    { value: NaN, label: "unset" },
    { value: 1.7777777777777777777777777777778, label: "16 : 9" },
    { value: 0.5625, label: "9 : 16" },
    { value: 1, label: "1 : 1" },
    { value: 1.3333333333333333333333333333333, label: "4 : 3" },
    { value: 0.75, label: "3 : 4" },
];

export interface ImageEditProps extends ImgI18NTranslatedComponentProps {
    open: boolean; // opens the Modal if true
    src: string;    // image as src
    aspect?: number;
    onCropFinish(data: Blob | string, width: number, height: number): void; // finished image
    onAbort?: () => void;
    additionalMoveButtons?: boolean;
    imageType?: string;
}

export interface ImageEditState {
    modalActive: boolean;
    imageCrop: IImageCrop | undefined; // link to cropping component
    imageFilter: IImageFilter | undefined; // link to filter component
    loading: boolean;
    currentEditMode: "crop" | "filter";
    filterToolIcons: [string, string][];
    originalSrc: string; // original source that gets passed in props, this shall never change 
    finalSrc: string; // final cropped and filtered image
    acquiredInfo: boolean;

    // cropper state
    cropperSrc: string; // the source of the image to crop | may be a filtered image after filter applied
    previewSrc: string; // src for filter preview 
    cropperImg: HTMLImageElement;
    cropperLoaded: boolean;
    cropperData?: Cropper.Data;
    imgData?: Cropper.ImageData;

    aspect: AspectOption;
}



class ImageEdit extends React.Component<ImageEditProps, ImageEditState> {

    constructor(props: ImageEditProps) {
        super(props);
        this.keyHit = this.keyHit.bind(this);
        this.state = {
            modalActive: this.props.open,
            imageCrop: undefined,
            imageFilter: this.getImageFilter(this.props.src),
            originalSrc: this.props.src,
            finalSrc: this.props.src,
            loading: false,
            currentEditMode: "crop",
            filterToolIcons: [],
            cropperSrc: this.props.src,
            previewSrc: this.props.src,
            cropperImg: new Image(),
            cropperLoaded: false,
            acquiredInfo: false,
            aspect: aspects[0]
        }

        this.initCropper(this.props.src);
    }

    componentDidMount() {
        document.addEventListener("keydown", this.keyHit, false);
    }
    componentWillUnmount() {
        document.removeEventListener("keydown", this.keyHit, false);
    }

    public componentWillReceiveProps = (newProps: ImageEditProps) => {
        this.setState({
            modalActive: newProps.open, // controls modal 
            originalSrc: newProps.src,
        });

        // if a new file is loaded, then reset all sources to new source.
        if (newProps.src !== this.state.originalSrc) {
            const newSrc = newProps.src;
            this.setState({
                originalSrc: newSrc,
                cropperSrc: newSrc,
                previewSrc: newSrc,
                finalSrc: newSrc,
                imageFilter: this.getImageFilter(newSrc)
            })
            this.initCropper(newSrc);
        }
    }

    public render() {
        const s = this.state;
        const t = this.props.t;
        const aspect = this.props.aspect ?? s.aspect.value;
        return (
            <ImgThemeConsumer>
                {(theme) => {
                    const isMobile = belowBreakpointOrEqual({ theme }, "tablet");
                    return (
                        <>
                            <Modal
                                active={s.modalActive}
                                icon="image"
                                header={this.state.currentEditMode === "filter" ? t("apply filter") : t("edit image")}
                                width={"full"}
                                height={isMobile ? "large" : "full"}
                                fixed
                                footer={
                                    <>
                                        <T.ActionButtons>
                                            {s.currentEditMode === "crop" && this.getCropTools()}
                                            {s.currentEditMode === "filter" && s.filterToolIcons.length >= 1 && this.getFilterTools()}

                                            {s.currentEditMode === "crop" &&
                                                <>
                                                    <Button
                                                        kind="primary"
                                                        icon="check"
                                                        content={isMobile ? undefined : t("save")}
                                                        onClick={this.onFinish}
                                                        size={"default"}
                                                        floated="right"
                                                    />
                                                    {!isMobile &&
                                                        <>
                                                            <T.Spacer3 /><T.Spacer4 />
                                                        </>
                                                    }
                                                </>
                                            }
                                            <Button
                                                kind={this.state.currentEditMode === "filter" ? "primary" : "halfTransparentButton"}
                                                icon={this.state.currentEditMode === "filter" ? "check" : "image effects"}
                                                floated="right"
                                                content={isMobile ? undefined : this.state.currentEditMode === "filter" ? t("adopt") : t("filter")}
                                                onClick={this.toggleEditMode}
                                                size={"default"}
                                            />
                                            {this.state.currentEditMode === "filter" && !isMobile &&
                                                <><T.Spacer3 /><T.Spacer4 /></>
                                            }
                                            {s.currentEditMode === "filter" && s.filterToolIcons.length >= 1 &&
                                                <Button
                                                    floated="right"
                                                    icon="check circle"
                                                    kind="halfTransparentButton"
                                                    onClick={this.applyFilter}
                                                    size={"default"}
                                                    content={isMobile ? undefined : t("apply")}
                                                />
                                            }
                                            {s.currentEditMode === "filter" && s.filterToolIcons.length >= 1 &&
                                                <Button
                                                    floated="right"
                                                    icon="undo"
                                                    kind="halfTransparentButton"
                                                    content={isMobile ? undefined : t("reset")}
                                                    onClick={this.resetFilter}
                                                    size={"default"}
                                                />
                                            }
                                            {s.currentEditMode === "crop" &&
                                                <Button
                                                    floated="right"
                                                    icon="undo"
                                                    kind="halfTransparentButton"
                                                    onClick={this.resetCrop}
                                                    size={"default"}
                                                    content={isMobile ? undefined : this.state.currentEditMode === "filter" ? t("reset") : t("reset")}
                                                />
                                            }
                                            {!isMobile &&
                                                <>
                                                    <T.Spacer3 /><T.Spacer4 />
                                                </>
                                            }
                                            <Button
                                                kind="halfTransparentButton"
                                                icon="times"
                                                floated="right"
                                                content={isMobile ? undefined : t("cancel")}
                                                onClick={this.onAbort}
                                                size={"default"}
                                            />
                                        </T.ActionButtons>

                                    </>
                                }
                            >
                                <Loader size={"small"} light active={this.state.loading} />
                                <ImageCrop
                                    src={this.state.cropperSrc}
                                    previewSrc={this.state.previewSrc}
                                    undoSrc={this.props.src}
                                    imageType={this.props.imageType ?? "png"}
                                    getImageCrop={this.getImageCrop}
                                    aspect={aspect}
                                    setState={this.updateState}
                                    loaded={this.state.cropperLoaded}
                                    getCropperData={this.getCropperData}
                                    cropperData={this.state.cropperData}
                                    filterMode={this.state.currentEditMode === "filter"}
                                />

                            </Modal >
                        </>
                    )
                }
                }
            </ImgThemeConsumer >
        );


    }

    private keyHit = (event: KeyboardEvent) => {
        if (event.code === 'Escape') {
            this.onAbort();
        }
    };

    private getImageFilter = (src: string): ImageFilter => {
        return (new ImageFilter({
            src,
            loading: this.loading,
            setFilterToolIcons: this.setFilterToolIcons,
            onPreview: this.onFilterPreview
        } as IImageFilterProps));
    }

    private getCropperData = (): Cropper.Data | undefined => {
        return this.state.cropperData;
    }

    private initCropper = (src: string) => {
        const { cropperImg } = this.state;
        cropperImg.src = src;
        cropperImg.onload = () => this.setState({ cropperLoaded: true });
    }

    private setFilterToolIcons = (filterToolIcons: [string, string][]) => {
        this.setState({ filterToolIcons });
    }

    private getCropTools = () => {
        return (
            <ImgThemeConsumer>
                {(theme) => {
                    const isMobile = belowBreakpointOrEqual({ theme }, "tablet");
                    return (
                        <div style={{ position: "relative" }}>
                            <T.Left>
                                <Tooltip tooltipText={this.props.t("rotate image 15° to the left")} offsetX={-35} position="top" noMargin >
                                    <Button icon="rotate left" kind="halfTransparentButton" onClick={this.onRotate("left")} size={"default"} />
                                </Tooltip>
                            </T.Left>
                            <T.Left>
                                <Tooltip tooltipText={this.props.t("rotate image 15° to the right")} offsetX={-35} position="top" noMargin >
                                    <Button floated="left" icon="rotate right" kind="halfTransparentButton" onClick={this.onRotate("right")} size={"default"} />
                                </Tooltip>
                            </T.Left>
                            {this.props.additionalMoveButtons && !isMobile &&
                                <>
                                    <T.Spacer1 /><T.Spacer2 />
                                    <T.Left><Tooltip tooltipText={this.props.t("move image left")} offsetX={-35} position="top" noMargin >
                                        <Button floated="left" icon="chevron left" kind="halfTransparentButton" onClick={this.onMove("left")} size={"default"} />
                                    </Tooltip></T.Left>
                                    <T.Left><Tooltip tooltipText={this.props.t("move image right")} offsetX={-35} position="top" noMargin >
                                        <Button floated="left" icon="chevron right" kind="halfTransparentButton" onClick={this.onMove("right")} size={"default"} />
                                    </Tooltip></T.Left>
                                    <T.Left><Tooltip tooltipText={this.props.t("move image up")} offsetX={-35} position="top" noMargin >
                                        <Button floated="left" icon="chevron up" kind="halfTransparentButton" onClick={this.onMove("up")} size={"default"} />
                                    </Tooltip></T.Left>
                                    <T.Left><Tooltip tooltipText={this.props.t("move image down")} offsetX={-35} position="top" noMargin >
                                        <Button floated="left" icon="chevron down" kind="halfTransparentButton" onClick={this.onMove("down")} size={"default"} />
                                    </Tooltip></T.Left>
                                    <T.Spacer1 /><T.Spacer2 />
                                </>
                            }
                            <T.Left><Tooltip tooltipText={this.props.t("mirror image horizontal")} offsetX={-35} position="top" noMargin >
                                <Button floated="left" icon="mirror horizontal" kind="halfTransparentButton" onClick={this.onMirror("horizontal")} size={"default"} />
                            </Tooltip></T.Left>
                            <T.Left><Tooltip tooltipText={this.props.t("mirror image vertical")} offsetX={-25} position="top" noMargin >
                                <Button floated="left" icon="mirror vertical" kind="halfTransparentButton" onClick={this.onMirror("vertical")} size={"default"} />
                            </Tooltip></T.Left>
                            {!isMobile && <>
                                <T.Spacer1 /><T.Spacer2 />
                                <T.Left><Tooltip tooltipText={this.props.t("zoom in")} offsetX={25} position="top" noMargin >
                                    <Button floated="left" icon="zoom in" kind="halfTransparentButton" onClick={this.onZoom("in")} size={"default"} />
                                </Tooltip></T.Left>
                                <T.Left><Tooltip tooltipText={this.props.t("zoom out")} offsetX={0} position="top" noMargin >
                                    <Button floated="left" icon="zoom out" kind="halfTransparentButton" onClick={this.onZoom("out")} size={"default"} />
                                </Tooltip></T.Left>
                                <T.Left><Tooltip tooltipText={this.props.t("reset zoom")} offsetX={0} position="top" noMargin >
                                    <Button floated="left" icon="maximize" kind="halfTransparentButton" onClick={this.onZoom("100")} size={"default"} />
                                </Tooltip></T.Left>
                                <T.Spacer1 /><T.Spacer2 />
                            </>
                            }
                            {this.props.aspect === undefined &&
                                <>
                                    <T.Left><Tooltip tooltipText={this.props.t("set aspect ratio")} offsetX={0} position="top" noMargin >
                                        <Button floated="left" icon="expand" kind="halfTransparentButton" size={"default"} />
                                    </Tooltip></T.Left>
                                    <T.AspectConatiner>
                                        <ImgSelect
                                            options={aspects}
                                            value={this.state.aspect}
                                            onChange={this.changeAspect}
                                            dropUp={true}
                                        />
                                    </T.AspectConatiner>
                                </>
                            }
                        </div>
                    )
                }
                }
            </ImgThemeConsumer >
        );
    }

    private changeAspect = (aspect: AspectOption) => {
        this.setState({ aspect: aspect });
        if (this.state.imageCrop)
            this.state.imageCrop.setAspectRatio(aspect.value);
    };

    private getFilterTools = () => {
        return (
            <T.FilterButtonContainer>
                {_.map(this.state.filterToolIcons, (icon, key) => {
                    return (
                        <T.FilterButtonElement key={key} image={icon[1]} onClick={this.filter(key)} >
                            <div>{icon[0]}</div>
                        </T.FilterButtonElement>
                    );
                })}
            </T.FilterButtonContainer>
        );
    }

    private loading = () => {
        this.setState({
            loading: true
        })
    }

    private onFilterPreview = (previewSrc: string) => {
        this.setState({
            previewSrc,
            loading: false
        });
    }

    private switchCropperToFilterMode = () => {
        if (this.state.imageCrop) {
            const cropperData = this.state.imageCrop.getData();
            const imgData = this.state.imageCrop.getImageData();
            this.setState({
                cropperData,
                imgData
            });
        }
        this.setState({
            imageFilter: this.getImageFilter(this.state.cropperSrc)
        })
    }

    private filter = (filter: number) => () => {
        if (this.state.imageFilter) {
            this.state.imageFilter.filter(filter);
        }
    }

    private applyFilter = () => {
        this.setState({
            imageFilter: this.getImageFilter(this.state.previewSrc),
            cropperSrc: this.state.previewSrc,
            loading: false,
            acquiredInfo: true
        })
    }

    private resetFilter = () => {
        this.setState({
            imageFilter: this.getImageFilter(this.state.originalSrc),
            previewSrc: this.state.originalSrc,
            cropperSrc: this.state.originalSrc,
            loading: false,
            acquiredInfo: false

        });
    }

    private toggleEditMode = () => {
        if (this.state.currentEditMode === "crop") {
            this.switchCropperToFilterMode();
            this.setState({ currentEditMode: "filter" });
        } else if (this.state.currentEditMode === "filter") {
            this.setState({ currentEditMode: "crop", imageFilter: this.getImageFilter(this.state.previewSrc), cropperSrc: this.state.previewSrc, loading: false, acquiredInfo: true });
        }
    }

    private resetCrop = (e: React.MouseEvent) => {
        if (this.state.imageCrop)
            this.state.imageCrop.reset();
    }
    private onAbort = () => {
        this.setState({ currentEditMode: "crop", modalActive: false })
        if (this.props.onAbort)
            this.props.onAbort();
    };
    private onFinish = (e: React.MouseEvent) => {
        this.saveImage();
        this.setState({ modalActive: false })
    }

    private onMirror = (direction: "horizontal" | "vertical") => () => {
        if (this.state.imageCrop)
            this.state.imageCrop.mirror(direction);
    }

    private onZoom = (direction: "in" | "out" | "100") => () => {
        if (this.state.imageCrop)
            this.state.imageCrop.zoom(direction);
    }

    private onMove = (direction: "right" | "left" | "up" | "down") => () => {
        if (this.state.imageCrop)
            this.state.imageCrop.move(direction);
    }

    private onRotate = (direction: "right" | "left") => () => {
        if (this.state.imageCrop)
            this.state.imageCrop.rotate(direction);
    }

    private getImageCrop = (imageCrop: IImageCrop) => {
        this.setState({ imageCrop });
    }

    private saveImage = () => {
        if (this.state.imageCrop) {
            this.setState({ loading: true });
            const ic = this.state.imageCrop;
            const data = ic.getData();
            // if (this.state.cropperData)
            // ic.setData(this.state.cropperData);
            ic.getBlob().then((blob) => {
                if (blob) {
                    this.setState({ loading: false });
                    this.props.onCropFinish(blob, data.width, data.height);
                }
            });
            ic.getURL().then((url) => {
                this.props.onCropFinish(url, data.width, data.height);
                this.setState({ finalSrc: url, modalActive: false })
            }
            );
        }
    }
    private updateState = (state: ImageEditState) => {
        this.setState(state);
    }
}
export default translate("general")(ImageEdit);